import {
  AfterViewInit, Component, ElementRef, Input, OnChanges, ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';

/**
 * A faint waveform under a timeline row: the recorded leg's 10 Hz loudness
 * curve (one byte per bin). Drawn on a fixed-width canvas that CSS stretches to
 * the row, so it lines up with the bars above it on the same clock.
 */
@Component({
  selector: 'app-envelope-track',
  standalone: true,
  template: '<canvas #c class="pointer-events-none absolute inset-0 h-full w-full opacity-60"></canvas>',
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'absolute inset-0 block' },
})
export class EnvelopeTrackComponent implements AfterViewInit, OnChanges {
  @Input() levels: Uint8Array = new Uint8Array(0);
  /** CSS colour of the bars. */
  @Input() color = '#4FA8F5';
  /** Track zoom level: the canvas gets proportionally more pixels. */
  @Input() zoom = 1;
  @ViewChild('c') private canvas?: ElementRef<HTMLCanvasElement>;

  private static readonly WIDTH = 1200;
  private static readonly HEIGHT = 24;

  ngAfterViewInit(): void { this.draw(); }
  ngOnChanges(): void { this.draw(); }

  private draw(): void {
    const el = this.canvas?.nativeElement;
    if (!el) return;
    const W = EnvelopeTrackComponent.WIDTH * Math.min(8, Math.max(1, this.zoom));
    const H = EnvelopeTrackComponent.HEIGHT;
    el.width = W;
    el.height = H;
    const ctx = el.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, W, H);
    const n = this.levels.length;
    if (!n) return;
    ctx.fillStyle = this.color;
    for (let x = 0; x < W; x++) {
      const from = Math.floor((x / W) * n);
      const to = Math.max(from + 1, Math.floor(((x + 1) / W) * n));
      let peak = 0;
      for (let i = from; i < to && i < n; i++) if (this.levels[i] > peak) peak = this.levels[i];
      const h = Math.max(1, (peak / 255) * H);
      ctx.fillRect(x, (H - h) / 2, 1, h);
    }
  }
}
